/**
 * Storage utilities for file access
 * Uses R2 storage via Cloudflare Worker API
 */

import { logger } from './logger';
import { getR2SignedUrl, getR2SignedUrls, isR2Url } from './r2Storage';

const BUCKET_NAME = 'staging-images' as const;
const SIGNED_URL_CACHE_TTL_MS = 5 * 60 * 1000;

type SignedUrlCacheEntry = {
  url: string;
  expiresAt: number;
};

const signedUrlCache = new Map<string, SignedUrlCacheEntry>();

function getCachedSignedUrl(path: string): string | null {
  const entry = signedUrlCache.get(path);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    signedUrlCache.delete(path);
    return null;
  }
  return entry.url;
}

function setCachedSignedUrl(path: string, url: string) {
  signedUrlCache.set(path, { url, expiresAt: Date.now() + SIGNED_URL_CACHE_TTL_MS });
}

/**
 * Generate a signed URL for a private storage file
 */
export async function getSignedUrl(filePath: string): Promise<string | null> {
  if (!filePath) return null;

  try {
    // If it's already a signed URL, return as-is
    if (isSignedUrl(filePath)) {
      return filePath;
    }

    // If it's already an R2 URL, return as-is
    if (isR2Url(filePath)) {
      return filePath;
    }

    // Get R2 signed URL
    const signedUrl = await getR2SignedUrl(BUCKET_NAME, filePath);
    
    if (!signedUrl) {
      logger.error('Failed to get R2 signed URL for:', filePath);
    }
    
    return signedUrl;
  } catch (err) {
    logger.error('Exception generating signed URL:', err);
    return null;
  }
}

/**
 * Cached signed URL for a private storage file.
 * Uses an in-memory TTL cache to avoid re-signing on every request.
 */
export async function getSignedUrlCached(filePath: string): Promise<string | null> {
  if (!filePath) return null;

  const cached = getCachedSignedUrl(filePath);
  if (cached) return cached;

  const signedUrl = await getSignedUrl(filePath);
  if (signedUrl) {
    setCachedSignedUrl(filePath, signedUrl);
  }
  return signedUrl;
}

/**
 * Generate signed URLs for multiple file paths
 * Uses batch R2 API for efficiency
 */
export async function getSignedUrls(filePaths: string[]): Promise<Map<string, string>> {
  const urlMap = new Map<string, string>();

  if (filePaths.length === 0) {
    return urlMap;
  }

  // Filter out already-signed URLs and R2 URLs
  const pathsNeedingSigning: string[] = [];
  
  for (const path of filePaths) {
    if (!path) continue;
    
    if (isSignedUrl(path) || isR2Url(path)) {
      // Already signed, add to map directly
      urlMap.set(path, path);
    } else {
      pathsNeedingSigning.push(path);
    }
  }

  if (pathsNeedingSigning.length === 0) {
    return urlMap;
  }

  try {
    // Get R2 signed URLs in batch
    const r2Urls = await getR2SignedUrls(BUCKET_NAME, pathsNeedingSigning);
    
    for (const [path, url] of r2Urls) {
      urlMap.set(path, url);
    }

    // Log any paths that didn't get URLs
    const missingPaths = pathsNeedingSigning.filter(p => !urlMap.has(p));
    if (missingPaths.length > 0) {
      logger.error(`Failed to get R2 signed URLs for ${missingPaths.length} paths:`, missingPaths);
    }
  } catch (err) {
    logger.error('Error getting batch signed URLs:', err);
    
    // Fallback: try getting URLs individually
    for (const path of pathsNeedingSigning) {
      if (!urlMap.has(path)) {
        const url = await getSignedUrl(path);
        if (url) {
          urlMap.set(path, url);
        }
      }
    }
  }

  return urlMap;
}

/**
 * Cached signed URLs for multiple file paths.
 * Only signs paths missing or expired in cache.
 */
export async function getSignedUrlsCached(filePaths: string[]): Promise<Map<string, string>> {
  const urlMap = new Map<string, string>();
  const pathsNeedingSigning: string[] = [];

  for (const path of filePaths) {
    if (!path) continue;

    const cached = getCachedSignedUrl(path);
    if (cached) {
      urlMap.set(path, cached);
      continue;
    }

    if (isSignedUrl(path) || isR2Url(path)) {
      urlMap.set(path, path);
      setCachedSignedUrl(path, path);
      continue;
    }

    pathsNeedingSigning.push(path);
  }

  if (pathsNeedingSigning.length > 0) {
    const signed = await getSignedUrls(pathsNeedingSigning);
    for (const [path, url] of signed) {
      urlMap.set(path, url);
      setCachedSignedUrl(path, url);
    }
  }

  return urlMap;
}

/**
 * Extract file path from a storage URL (removes the public URL prefix)
 * Handles both Supabase and R2 URLs
 *
 * Supports paths in the format:
 * - {userId}/{projectId}/original/{filename}
 * - {userId}/{projectId}/processed/{stagedImageId}.ext
 * - Legacy: {userId}/{projectId}/{prefix}-{timestamp}-{randomId}.ext
 */
export function extractFilePath(url: string): string {
  // If it's already a path (no http), return as is
  if (!url.startsWith('http')) {
    return url;
  }

  // Handle R2 signed URLs
  if (isR2Url(url)) {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('path') || url;
    } catch {
      return url;
    }
  }

  // Handle Supabase URLs - extract the path after /storage/v1/object/public/staging-images/
  const match = url.match(/\/storage\/v1\/object\/(?:public|sign)\/staging-images\/(.+?)(?:\?|$)/);
  if (match && match[1]) {
    return match[1];
  }

  // Fallback: return the URL as-is
  return url;
}

/**
 * Check if a path is in the new original format: {userId}/{projectId}/original/...
 */
export function isOriginalImagePath(path: string): boolean {
  return /^[^/]+\/[^/]+\/original\//.test(path);
}

/**
 * Check if a path is in the new processed format: {userId}/{projectId}/processed/...
 */
export function isProcessedImagePath(path: string): boolean {
  return /^[^/]+\/[^/]+\/processed\//.test(path);
}

/**
 * Check if a URL is already a signed URL
 * Handles both Supabase signed URLs (token=) and R2 signed URLs
 */
export function isSignedUrl(url: string): boolean {
  if (!url) return false;
  
  // Supabase signed URLs contain token=
  if (url.includes('token=')) return true;
  
  // R2 signed URLs contain /storage/serve?
  if (isR2Url(url)) return true;
  
  return false;
}
